import { useState } from 'react';
import { api, Organization } from '../api';
import { getErrorMessage } from '../../../shared/src';

interface AuthMethodPanelProps {
  org: Organization;
  onUpdate: (org: Organization) => void;
}

export function AuthMethodPanel({ org, onUpdate }: AuthMethodPanelProps) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const currentMethod = org.auth_method || 'sso';
  const hasExistingSsoConfig = org.sso_configs && org.sso_configs.length > 0 && org.sso_configs.some(c => c.is_enabled);

  const handleToggle = async (newMethod: 'sso' | 'password') => {
    if (newMethod === currentMethod) return;

    // Confirm the change
    const confirmMsg = newMethod === 'password'
      ? 'Switch to email/password authentication? This will disable SSO for this organization. Existing users will need to be invited with a password.'
      : 'Switch to SSO authentication? Users will need to login via SSO. You will need to configure an SSO provider.';

    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    setMessage('');
    try {
      const result = await api.updateAuthMethod(org.id, newMethod);
      setMessage(result.message);
      onUpdate(result.organization);
    } catch (err: unknown) {
      setMessage(`Error: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="auth-method-panel">
        <h3>Authentication Method</h3>
        <p className="description">
          Choose how users in this organization will authenticate. Organizations can use either SSO or email/password authentication, but not both.
        </p>

        <div className="auth-options">
          <div
            className={`auth-option ${currentMethod === 'sso' ? 'selected' : ''}`}
            onClick={() => !saving && handleToggle('sso')}
          >
            <div className="option-header">
              <div className="option-radio">
                <input
                  type="radio"
                  checked={currentMethod === 'sso'}
                  onChange={() => handleToggle('sso')}
                  disabled={saving}
                />
              </div>
              <div className="option-title">Single Sign-On (SSO)</div>
            </div>
            <div className="option-description">
              Users authenticate via your identity provider (Okta, Azure AD, Google Workspace, etc.).
              Requires SSO configuration in the next tab.
            </div>
            {currentMethod === 'sso' && !hasExistingSsoConfig && (
              <div className="option-warning">
                SSO is not yet configured. Configure it in the SSO tab.
              </div>
            )}
          </div>

          <div
            className={`auth-option ${currentMethod === 'password' ? 'selected' : ''}`}
            onClick={() => !saving && handleToggle('password')}
          >
            <div className="option-header">
              <div className="option-radio">
                <input
                  type="radio"
                  checked={currentMethod === 'password'}
                  onChange={() => handleToggle('password')}
                  disabled={saving}
                />
              </div>
              <div className="option-title">Email/Password</div>
            </div>
            <div className="option-description">
              Users authenticate with their email and password.
              You can invite users and manage their access in the Users tab.
            </div>
            {currentMethod === 'password' && (
              <div className="option-info">
                To add users, go to the Users tab and click "Invite User".
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className={message.startsWith('Error') ? 'error-message' : 'success-message'}>
            {message}
          </div>
        )}

        {saving && <div className="saving-indicator">Updating...</div>}
      </div>

      <style>{`
        .auth-method-panel {
          max-width: 600px;
        }

        .auth-method-panel h3 {
          margin-bottom: 8px;
        }

        .auth-method-panel .description {
          color: #666;
          margin-bottom: 24px;
        }

        .auth-options {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .auth-option {
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          padding: 16px;
          cursor: pointer;
          transition: border-color 0.2s, background-color 0.2s;
        }

        .auth-option:hover {
          border-color: #4a90a4;
        }

        .auth-option.selected {
          border-color: #4a90a4;
          background-color: #f8fbfc;
        }

        .option-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .option-radio input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .option-title {
          font-weight: 600;
          font-size: 16px;
        }

        .option-description {
          color: #666;
          font-size: 14px;
          margin-left: 30px;
        }

        .option-warning {
          margin-top: 12px;
          margin-left: 30px;
          padding: 8px 12px;
          background-color: #fff3cd;
          border-radius: 4px;
          color: #856404;
          font-size: 13px;
        }

        .option-info {
          margin-top: 12px;
          margin-left: 30px;
          padding: 8px 12px;
          background-color: #e8f4f8;
          border-radius: 4px;
          color: #0c5460;
          font-size: 13px;
        }

        .saving-indicator {
          margin-top: 16px;
          color: #666;
          font-style: italic;
        }

      `}</style>
    </div>
  );
}
